import { useEffect, useState } from "react";

const NAV_OFFSET = 88;

const dataAttributeForKey = (dataKey) =>
  `data-${dataKey.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;

const usePrimaryNavSection = ({
  enabled,
  defaultKey,
  selector = "[data-primary-nav]",
  dataKey = "primaryNav",
  activationOffset = NAV_OFFSET,
}) => {
  const [activeKey, setActiveKey] = useState(defaultKey);

  useEffect(() => {
    setActiveKey(defaultKey);
    if (!enabled) return undefined;

    let frameId;
    let cancelled = false;

    const update = () => {
      if (cancelled) return;

      const sections = Array.from(document.querySelectorAll(selector));
      if (!sections.length) {
        setActiveKey(defaultKey);
        return;
      }

      const atActivationLine = sections.map((section) => {
        const rect = section.getBoundingClientRect();
        return { section, rect };
      }).filter(
        ({ rect }) =>
          rect.top <= activationOffset && rect.bottom > activationOffset
      );

      const currentMarker = atActivationLine.reduce((current, candidate) => {
        if (!current) return candidate;
        const currentHeight = current.rect.height ?? current.rect.bottom - current.rect.top;
        const candidateHeight =
          candidate.rect.height ?? candidate.rect.bottom - candidate.rect.top;
        if (candidateHeight <= currentHeight) return candidate;
        return current;
      }, null);
      setActiveKey(currentMarker?.section.dataset[dataKey] || defaultKey);
    };

    const scheduleUpdate = () => {
      if (cancelled) return;
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(update);
    };

    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: [dataAttributeForKey(dataKey)],
      childList: true,
      subtree: true,
    });

    scheduleUpdate();
    document.fonts?.ready.then(scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("pageshow", scheduleUpdate);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      mutationObserver.disconnect();
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("pageshow", scheduleUpdate);
    };
  }, [activationOffset, dataKey, defaultKey, enabled, selector]);

  return activeKey;
};

export default usePrimaryNavSection;
